import { useState, useRef, useEffect } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface MultiCheckboxSelectProps<T extends string> {
  options: { label: string; value: T }[];
  value: T[];
  onChange: (value: T[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function MultiCheckboxSelect<T extends string>({
  options,
  value,
  onChange,
  placeholder = "Select...",
  disabled,
  className,
}: MultiCheckboxSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  const selectedLabels = options
    .filter((o) => value.includes(o.value))
    .map((o) => o.label);

  const displayText =
    selectedLabels.length > 0 ? selectedLabels.join(", ") : placeholder;

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        disabled={disabled}
        className={cn(
          "flex h-9 w-full items-center justify-between border border-input bg-background px-3 py-2 text-sm",
          "focus:outline-none focus:ring-1 focus:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
        onClick={() => setOpen(!open)}
      >
        <span className="truncate text-left flex-1">{displayText}</span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
      </button>
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full border border-input bg-background shadow-md">
          <div className="p-2 space-y-1">
            {options.map((option) => {
              const isChecked = value.includes(option.value);
              return (
                <div
                  key={option.value}
                  className="flex items-center gap-2 px-1 py-1 hover:bg-muted cursor-pointer"
                  onClick={() => {
                    const next = isChecked
                      ? value.filter((v) => v !== option.value)
                      : [...value, option.value];
                    onChange(next);
                  }}
                >
                  <Checkbox checked={isChecked} tabIndex={-1} />
                  <Label className="font-normal cursor-pointer text-sm">
                    {option.label}
                  </Label>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
